import { InputBase } from "./../models/InputBase";
import VueRouter, { Route } from "vue-router";
import store from "./../src/store/index";
import axios, { AxiosError } from "axios";
import {
  SnackbarColors,
  SnackbarBody,
  RequestBodyErrorPayload
} from "./../models/MiscTypes";
import Vue from "vue";
import cloneDeep from "lodash/cloneDeep";
import Wraxios from "./Wraxios";
import { Translations } from "translations/Translations";
import eventTracking from "./EventTracking";

export function isProfilePagePath(path: string): boolean {
  return path === "/UserProfile";
}

export function allDependenciesHaveValues(
  dependencies: string[],
  formId: string
): boolean {
  const requestBody = store.getters.get_newRequestBody[formId];

  for (const dependency of dependencies) {
    if (
      !requestBody[dependency] ||
      !(requestBody[dependency] as string).trim()
    ) {
      return false;
    }
  }

  return true;
}

export function setButtonWidth(className: string): void {
  let maxWidth = 0;
  const buttons = Array.from(
    document.getElementsByClassName(className)
  ) as HTMLButtonElement[];

  buttons.forEach(button => {
    if (button.offsetWidth > maxWidth) {
      maxWidth = button.offsetWidth;
    }
  });

  buttons.forEach(button => {
    button.style.width = `${maxWidth}px`;
  });
}



export function addGlow(maxAttribute: string, formId: string) {
  const el = document.getElementById(
    `${maxAttribute}-border-${formId}`
  ) as HTMLElement;

  if (!!el) {
    el.classList.add("related-data-change");
    setTimeout(() => {
      el.classList.remove("related-data-change");
    }, 390);
  }
}

export function getQueryParam(param: string): string {
  const queryParams = new URLSearchParams(window.location.search);
  return (queryParams.get(param) as string) || "";
}

export function getPageName(route: Route): string {
  try {
    const pageNameParam = getQueryParam("pageName");

    let pageName =
      pageNameParam !== ""
        ? pageNameParam
        : route.path !== "" && route.path !== undefined
        ? route.path
        : ("" as string);

    if (pageName.includes("/")) {
      pageName = pageName.replace("/", "");
    }

    return pageName;
  } catch (error) {
    return "";
  }
}

// This function is responsible for dealing with errors
// returned from Maximo. A copy of the pageInputs object
// is made and then filtered to isolate the input that
// has the error, and then the filtered input has its
// isErrorState property set to true. A snackbar is shown
// to let the user know there's a problem with the form.
export function badInput(
  // eslint-disable-next-line
  error: AxiosError,
  inputs: InputBase[],
  formId: string,
  snackbarText?: string
) {
  const translations: Translations = store.getters.get_translations;

  if (error.response) {
    const badInputId = error.response.headers.badinputid as string;

    if (badInputId) {
      const updatedInputData = cloneDeep(inputs);
      updatedInputData.filter(
        x => x.maxAttribute === badInputId
      )[0].isErrorState = true;
      const payload = {
        formId: formId,
        payload: { [badInputId]: true }
      } as RequestBodyErrorPayload;
      store.commit("set_newRequestBodyErrors", payload);

      const snackbarBody = {
        text: error.response.data ? error.response.data : snackbarText,
        color: SnackbarColors.error
      } as SnackbarBody;

      Vue.nextTick(() => {
        store.commit("push_snackbar", {
          ...snackbarBody
        });
      });

      return updatedInputData;
    }

    return inputs;
  }

  Vue.nextTick(() => {
    store.commit("push_snackbar", {
      text: translations.problemWithDataSent,
      color: SnackbarColors.error
    });
  });

  return [];
}

export function arraysAreSameLength(
  newArray: InputBase[],
  oldArray: InputBase[],
  snackbarText: string
): boolean {
  // This function's primary use is to make sure that the length
  // of the array returned from the badInput function is the same
  // length as the inputs array on the input form. If they're not
  // the same length, then there's been a problem somewhere and
  // we need to let the user know.

  if (newArray.length !== oldArray.length) {
    store.commit("push_snackbar", {
      text: snackbarText,
      color: SnackbarColors.error
    });

    return false;
  }

  return true;
}

export function checkForMissingInputs(pageInputs: InputBase[], formId: string) {
  const translations: Translations = store.getters.get_translations;
  const requestBody = store.getters.get_newRequestBody[formId];
  const requiredInputIds = [] as string[];
  // Get the inputs that are required and store the element ID that
  // corresponds to the input for later use.
  pageInputs
    .filter(x => !!x.rules.required)
    .forEach(i => requiredInputIds.push(i.maxAttribute));

  for (const inputId of requiredInputIds) {
    const inputToCheck = pageInputs.filter(x => x.maxAttribute === inputId);

    // Check if the value of the input is empty or undefined. If it is,
    // then we return the label of the input that is in the error state
    if (requestBody[inputId] === "" || requestBody[inputId] === undefined) {
      const payload = {
        formId: formId,
        payload: { [inputToCheck[0].maxAttribute]: true }
      } as RequestBodyErrorPayload;

      Vue.nextTick(() => {
        store.commit("set_newRequestBodyErrors", payload);
        const snackbarBody: SnackbarBody = {
          text: `${inputToCheck[0].label} ${translations.isRequired}.`,
          color: SnackbarColors.error
        };
        store.dispatch("do_showSnackbar", snackbarBody);
      });

      return inputToCheck[0].label;
    }
  }

  return "";
}

export function isComponentError(maxAttribute: string, formId: string) {
  let isErrorState = false;
  const id = `${formId}${maxAttribute}`;

  if (
    store.getters.get_newRequestBodyErrors[formId] &&
    store.getters.get_newRequestBodyErrors[formId][maxAttribute] === true
  ) {
    isErrorState = true;
    (document.getElementById(id) as HTMLElement).classList.add(
      "input-label-error"
    );
  }

  return isErrorState;
}

// Based on this stack overflow post. Modifications have been made:
// https://stackoverflow.com/a/1349426
export function generateRandomString(length: number): string {
  // += on strings is O(n^2), so instead we create an array of individual characters and then join them
  const charArray = [];
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < length; i++) {
    charArray.push(
      characters.charAt(Math.floor(Math.random() * characters.length))
    );
  }

  return charArray.join("");
}

// Generates the form id to be used with the newRequestBody in the store
export function generateFormId(router: VueRouter): string {
  const translations: Translations = store.getters.get_translations;
  const currentIdsInUse = Object.keys(store.getters.get_newRequestBody);
  const generationAttempts = 1000; // Amount of times to attempt to generate the key.

  let formId;
  let count = 0;

  do {
    formId = generateRandomString(20);
    count++;
  } while (currentIdsInUse.includes(formId) && count < generationAttempts);

  if (count >= 1000) {
    const snackbarBody = {
      text: translations.problemLoadingTheForm,
        
      color: SnackbarColors.error
    } as SnackbarBody;

    Vue.nextTick(() => {
      store.commit("push_snackbar", {
        ...snackbarBody
      });
    });

    router.push("/");
    throw "There was an error generating your form. Please contact support";
  } else {
    return formId;
  }
}

export function removeErrorState(instance: Vue) {
  instance.$emit("input-error", false);
  const errorPayload = {
    formId: instance.$props.formId,
    payload: { [instance.$props.inputData.maxAttribute]: false }
  } as RequestBodyErrorPayload;
  store.commit("set_newRequestBodyErrors", errorPayload);
  instance.$props.inputData.isErrorState = false;
  instance.$data.isErrorState = false;
  const label = document.getElementById(instance.$data.labelId) as HTMLElement;

  if (label) {
    label.classList.remove("input-label-error");
  }
}

export function setErrorState(instance: Vue): void {
  instance.$data.isErrorState = true;
  instance.$emit("input-error", true);
  (document.getElementById(
    instance.$data.labelId
  ) as HTMLElement).classList.add("input-label-error");

  const payload = {
    formId: instance.$props.formId,
    payload: { [instance.$props.inputData.maxAttribute]: true }
  } as RequestBodyErrorPayload;
  store.commit("set_newRequestBodyErrors", payload);
}

// The expected format of the date from the server is "mm/dd/yyyy hh:mm:ss am/pm"
// and should be the same whether extracting a minimum or maximum date.
export function getMinMaxDate(date: string): Date {
  return new Date(date.substring(0, date.indexOf(" ")));
}

export async function logUserOut(router: VueRouter, isRegistration: boolean) {
  const translations: Translations = store.getters.get_translations;
  window.localStorage.clear();
  window.sessionStorage.clear();
  eventTracking.writeLogoutEvent(false);
  eventTracking.stopSendingData();
  store.commit("set_userIsAuthenticated", false);
  store.commit("set_userData", {});
  store.commit("set_userLoginTime", "");

  const sessionHash = store.getters.get_userSessionHash;

  function handle200(isRegistration: boolean) {
    store.commit("set_userSessionHash", "");

    if (!isRegistration) {
      const snackbarBody = {
        text: translations.successfullyLoggedOut,
        color: SnackbarColors.success
      } as SnackbarBody;

      Vue.nextTick(() => {
        store.commit("push_snackbar", {
          ...snackbarBody
        });
      });
    }

    router.push("/");
  }

  function handleError(isRegistration: boolean) {
    store.commit("set_userSessionHash", "");

    if (!isRegistration) {
      const snackbarBody = {
        text: translations.failedToLogOut,
        color: SnackbarColors.error
      } as SnackbarBody;

      Vue.nextTick(() => {
        store.commit("push_snackbar", {
          ...snackbarBody
        });
      });
    }

    router.push("/");
  }

  const wraxios = new Wraxios();
  await wraxios.post(
    axios,
    `account/logout?sessionHash=${sessionHash}`,
    {},
    {
      200: () => handle200(isRegistration),
      400: () => handleError(isRegistration),
      500: () => handleError(isRegistration)
    }
  );
}
